/**
 * Canonical legal identity of the company operating ZETL CRM.
 *
 * Every § 5 DDG / Art. 13 GDPR datum the website publishes about its
 * operator — firm name, business address, contact channels, managing
 * directors — is declared here exactly once. The Impressum, the privacy
 * policy, the AGB, the AVV, the accessibility statement, the contact page,
 * the footer and the JSON-LD graph all resolve to this module rather than
 * repeating the strings, so a change of firm designation or address is a
 * one-line edit that cannot leave one page contradicting another.
 *
 * Static HTML reaches these values through `{{company.<field>}}` tokens
 * substituted at build/dev time by `plugins/company-placeholders.ts`;
 * TypeScript (translations, JSON-LD) imports `COMPANY` directly.
 *
 * The company is the contracting party of every agreement the site publishes —
 * the AGB, the AVV and the privacy policy name the company itself, never its
 * shareholders or managing directors. `company.spec.ts` guards that no page
 * keeps its own copy of any value declared here.
 */
export interface CompanyIdentity {
  /** Firm name as it must appear in the Anbieterkennzeichnung (§ 5 Abs. 1 Nr. 1 DDG). */
  readonly legalName: string;
  /** Product/brand name — deliberately separate from the firm name. */
  readonly brandName: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  /** Country of the registered office, per site language. */
  readonly country: { readonly de: string; readonly en: string };
  /** Phone number in readable form. */
  readonly phone: string;
  /** Same number, normalised for `tel:` hrefs. */
  readonly phoneHref: string;
  readonly email: string;
  /** Dedicated inbox named in the accessibility statement (BFSG). */
  readonly accessibilityEmail: string;
  readonly website: string;
  /** Vertretungsberechtigte (§ 5 Abs. 1 Nr. 1 DDG). */
  readonly managingDirectors: readonly string[];
  /** Registergericht (§ 5 Abs. 1 Nr. 4 DDG). */
  readonly registerCourt: string;
  /** Handelsregisternummer (§ 5 Abs. 1 Nr. 4 DDG). */
  readonly registerNumber: string;
}

export const COMPANY: CompanyIdentity = {
  legalName: 'Amberon Software UG (haftungsbeschränkt)',
  brandName: 'ZETL CRM',
  street: 'Klingenhof 224',
  postalCode: '74535',
  city: 'Mainhardt',
  country: { de: 'Deutschland', en: 'Germany' },
  phone: '+49 711 16128522',
  phoneHref: '+4971116128522',
  email: 'info@zetlcrm.com',
  accessibilityEmail: 'info@zetlcrm.com',
  website: 'https://zetlcrm.com',
  managingDirectors: ['Stefan Stelzer', 'Tim Laine'],
  registerCourt: 'Amtsgericht Stuttgart',
  registerNumber: 'HRB 808232',
} as const;

/** `74535 Mainhardt` — postal code and city as one line. */
export const companyCityLine = (): string => `${COMPANY.postalCode} ${COMPANY.city}`;

/** `Stefan Stelzer, Tim Laine` — as listed under "Vertreten durch". */
export const companyDirectors = (): string => COMPANY.managingDirectors.join(', ');

/** `Klingenhof 224, 74535 Mainhardt` — address on a single line, no country. */
export const companyAddressInline = (): string => `${COMPANY.street}, ${companyCityLine()}`;

/** Address as `<br>`-separated HTML lines, including the country. */
export const companyAddressHtml = (lang: 'de' | 'en'): string =>
  [COMPANY.street, companyCityLine(), COMPANY.country[lang]].join('<br>');

/**
 * The `{{company.<field>}}` tokens static HTML may use, resolved against
 * the German site language (the pre-rendered English pages take their copy
 * from the translation dictionary instead, never from these fallbacks).
 */
export const COMPANY_PLACEHOLDERS: Readonly<Record<string, string>> = {
  'company.legalName': COMPANY.legalName,
  'company.brandName': COMPANY.brandName,
  'company.street': COMPANY.street,
  'company.postalCode': COMPANY.postalCode,
  'company.city': COMPANY.city,
  'company.cityLine': companyCityLine(),
  'company.country': COMPANY.country.de,
  'company.addressInline': companyAddressInline(),
  'company.phone': COMPANY.phone,
  'company.phoneHref': COMPANY.phoneHref,
  'company.email': COMPANY.email,
  'company.accessibilityEmail': COMPANY.accessibilityEmail,
  'company.website': COMPANY.website,
  'company.managingDirectors': companyDirectors(),
  'company.registerCourt': COMPANY.registerCourt,
  'company.registerNumber': COMPANY.registerNumber,
} as const;
